import { map } from 'rxjs/operators';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { StableLoad } from '../_models/stableLoad';

@Injectable()
export class StableLoadsService {
  // Services
  private http = inject(HttpClient);
  private apiBaseUrl = environment.apiBaseUrl;
  stableLoadList$: BehaviorSubject<StableLoad[]> = new BehaviorSubject<StableLoad[]>([]);
  destinationUseList = [
    'milk_raw_cow',
    'milk_raw_cow_hq',
    'milk_bio_cow',
    'milk_parmesan_brown',
    'milk_parmesan_bio',
    'milk_parmesan',
    'milk_beta_casein',
    'milk_sheep',
    'milk_buffalo',
    'milk_goat',
    'milk_hay_stg',
    'milk_grana_padano',
  ];

  constructor() {}

  getStableLoadsFromLocalStore(): Observable<StableLoad[]> {
    return this.stableLoadList$;
  }

  getStableLoads(): Observable<StableLoad[]> {
    return this.http.get<any>(this.apiBaseUrl + 'api/private/v1/stableLoads').pipe(
      map((response) => {
        this.stableLoadList$.next(response);
        return response;
      })
    );
  }

  getStableLoad(id: number): Observable<StableLoad> {
    return this.http
      .get<StableLoad>(this.apiBaseUrl + 'api/private/v1/stableLoads/' + id)
      .pipe(map((response) => response));
  }

  getStableLoadsByFilters(filters = []): Observable<StableLoad[]> {
    const searchParams = new URLSearchParams();

    filters.forEach(({ label, values }) => {
      values.forEach((v) => searchParams.append(label, v));
    });

    return this.http
      .get<StableLoad[]>(this.apiBaseUrl + 'api/private/v2/stableLoads?' + searchParams.toString())
      .pipe(map((response) => response));
  }

  getStableLoadsByCompanyId(companyId: number): Observable<StableLoad[]> {
    return this.http
      .get<StableLoad[]>(this.apiBaseUrl + 'api/private/v2/stableLoads?companyIds=' + companyId)
      .pipe(map((response) => response));
  }

  saveStableLoad(loadItem): Observable<StableLoad> {
    const load = { ...loadItem };

    delete load.lastUpdated;
    delete load.dateCreated;

    if (load.user) {
      const iduser: any = {
        id: load.user.id,
      };
      load.user = iduser;
    }

    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    load.companyId = companySelected ? companySelected.id : null;
    load.isAutomaticImportEnabled = true;

    if (load.id) {
      return this.http
        .put<StableLoad>(this.apiBaseUrl + 'api/private/v1/stableLoads/' + load.id, load)
        .pipe(map((response) => response));
    } else {
      return this.http
        .post<StableLoad>(this.apiBaseUrl + 'api/private/v1/stableLoads/', load)
        .pipe(map((response) => response));
    }
  }

  deleteStableLoad(load: StableLoad): Observable<any> {
    return this.http.delete(this.apiBaseUrl + 'api/private/v1/stableLoads/' + load.id).pipe(
      map(() => {
        return { id: load.id };
      })
    );
  }

  getDailyProduction(paramenters: Array<string>, startDate, endDate, stables = []) {
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    const companyId = companySelected ? companySelected.id : null;
    const body = {
      companyIds: companyId,
      startDate,
      endDate,
      parameters: paramenters,
      connectionType: 'OWN',
      stables,
    };

    return this.http
      .post(this.apiBaseUrl + 'api/private/v1/stableLoads/analitics/dailyProduction', body)
      .pipe(map((response) => response));
  }

  getCumulativeMontlyProduction(
    paramenters: Array<string>,
    startDate,
    endDate,
    connectionType = 'OWN',
    stableAslCode = null,
    externalId = null,
    stables = []
  ): Observable<any> {
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    const companyId = companySelected ? companySelected.id : null;
    const body = {
      companyIds: companyId,
      startDate,
      endDate,
      parameters: paramenters,
      connectionType,
      stables,
      ...(stableAslCode && { aslCode: stableAslCode }),
      ...(externalId && { externalId }),
    };

    return this.http
      .post(
        this.apiBaseUrl + 'api/private/v1/stableLoads/analitics/cumulativeMontlyProduction',
        body
      )
      .pipe(map((response) => response));
  }

  getDailyFat(
    paramenters: Array<string>,
    startDate,
    endDate,
    connectionType,
    stables = []
  ): Observable<any> {
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    const companyId = companySelected ? companySelected.id : null;
    const body = {
      companyIds: companyId,
      startDate,
      endDate,
      parameters: paramenters,
      connectionType,
      stables,
    };

    return this.http
      .post(this.apiBaseUrl + 'api/private/v1/stableLoads/analitics/dailyFat', body)
      .pipe(map((response) => response));
  }

  getDailyProteins(
    paramenters: Array<string>,
    startDate,
    endDate,
    connectionType,
    stables = []
  ): Observable<any> {
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    const companyId = companySelected ? companySelected.id : null;
    const body = {
      companyIds: companyId,
      startDate,
      endDate,
      parameters: paramenters,
      connectionType,
      stables,
    };

    return this.http
      .post(this.apiBaseUrl + 'api/private/v1/stableLoads/analitics/dailyProteins', body)
      .pipe(map((response) => response));
  }

  getDailyBacterialCharge(
    paramenters: Array<string>,
    startDate,
    endDate,
    connectionType,
    stables = []
  ): Observable<any> {
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    const companyId = companySelected ? companySelected.id : null;
    const body = {
      companyIds: companyId,
      startDate,
      endDate,
      parameters: paramenters,
      connectionType,
      stables,
    };

    return this.http
      .post(this.apiBaseUrl + 'api/private/v1/stableLoads/analitics/dailyBacterialCharge', body)
      .pipe(map((response) => response));
  }

  getDailySomaticCells(
    paramenters: Array<string>,
    startDate,
    endDate,
    connectionType,
    requestedCompanyId = null,
    stables = []
  ): Observable<any> {
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    const companyId = requestedCompanyId
      ? requestedCompanyId
      : companySelected
        ? companySelected.id
        : null;
    const body = {
      companyIds: companyId,
      startDate,
      endDate,
      parameters: paramenters,
      connectionType,
      stables,
    };

    return this.http
      .post(this.apiBaseUrl + 'api/private/v1/stableLoads/analitics/dailySomaticCells', body)
      .pipe(map((response) => response));
  }

  getProteinCasein(paramenters: Array<string>, startDate, endDate, connectionType, stables = []) {
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    const companyId = companySelected ? companySelected.id : null;
    const body = {
      companyIds: companyId,
      startDate,
      endDate,
      parameters: paramenters,
      connectionType,
      stables,
    };

    return this.http
      .post(this.apiBaseUrl + 'api/private/v1/stableLoads/analitics/casein', body)
      .pipe(map((response) => response));
  }

  getDairyEfficency(
    paramenters: Array<string>,
    startDate,
    endDate,
    connectionType,
    stables = []
  ): Observable<any> {
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    const companyId = companySelected ? companySelected.id : null;
    const body = {
      companyIds: companyId,
      startDate,
      endDate,
      parameters: paramenters,
      connectionType,
      stables,
    };

    return this.http
      .post(this.apiBaseUrl + 'api/private/v1/stableLoads/analitics/efficency', body)
      .pipe(map((response) => response));
  }

  getTresholdChart(chartType) {
    return this.http
      .get<any>(
        this.apiBaseUrl + 'api/private/v1/stableLoads/analitics/' + chartType + '/graphThreshold'
      )
      .pipe(map((response) => response));
  }

  setTresholdChart(chartType, franchise, prize) {
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    const companyId = companySelected ? companySelected.id : null;
    const body = { companyIds: companyId, franchise, prize };
    return this.http
      .post(
        this.apiBaseUrl + 'api/private/v1/stableLoads/analitics/' + chartType + '/graphThreshold',
        body
      )
      .pipe(map((response) => response));
  }

  getStableLoadFilters(year = null) {
    const params = new URLSearchParams();

    if (year) {
      params.set('year', year);
    }

    return this.http
      .get(`${this.apiBaseUrl}api/private/v2/stableLoads/filters?${params.toString()}`)
      .pipe(map((response) => response));
  }
}
